import React, { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import oneSpaceApi from "../../lib/onespace-api";
import { Trash2, Plus, CheckCircle, Loader2, Maximize2, Minimize2, X, EyeOff, Eye } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface Note {
  id?: string;
  content: string;
  created_at?: string;
}

interface NotesWidgetProps {
  id: string;
  title: string;
}

// Local storage key for blur state
const BLUR_STORAGE_KEY = "onespace_notes_blur_state";

const NotesWidget: React.FC<NotesWidgetProps> = ({ id, title }) => {
  const { isAuthenticated } = useAuth();
  const [noteText, setNoteText] = useState("");
  const [savedNotes, setSavedNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [isBlurred, setIsBlurred] = useState(() => {
    // Initialize from localStorage
    const storedBlurState = localStorage.getItem(BLUR_STORAGE_KEY);
    return storedBlurState !== null ? JSON.parse(storedBlurState) : false;
  });
  
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Delete state
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Update localStorage when blur state changes
  useEffect(() => {
    localStorage.setItem(BLUR_STORAGE_KEY, JSON.stringify(isBlurred));
  }, [isBlurred]);

  // Fetch notes from API
  const fetchNotes = async () => {
    if (!isAuthenticated) {
      setError("Je moet ingelogd zijn om notities te bekijken.");
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await oneSpaceApi.get('/api/notes');
      console.log('API Response:', response.data);

      let notesArray: Note[] = [];

      // Handle different response formats
      if (response.data) {
        if (Array.isArray(response.data)) {
          // If response.data is an array
          notesArray = response.data;
        } else if (Array.isArray(response.data.data)) {
          // If response.data.data is an array
          notesArray = response.data.data;
        } else if (response.data.data && typeof response.data.data === 'object') {
          // If response.data.data is a single object
          notesArray = [response.data.data];
        } else if (typeof response.data === 'object') {
          // If response.data is a single object
          notesArray = [response.data];
        }
      }

      setSavedNotes(notesArray);
      
      // Automatically select the most recent note if there are any
      if (notesArray.length > 0) {
        // Sort notes by created_at if available, newest first
        const sortedNotes = [...notesArray].sort((a, b) => {
          if (!a.created_at && !b.created_at) return 0;
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        const latestNote = sortedNotes[0];
        setActiveNoteId(latestNote.id || null);
        setNoteText(latestNote.content);
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
      setError("Kan notities niet laden. Probeer het later opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  // Load notes on component mount
  useEffect(() => {
    fetchNotes();
  }, [id, isAuthenticated]);
  
  // Focus textarea when going fullscreen
  useEffect(() => {
    if (isFullscreen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isFullscreen]);
  
  // Auto-save on changes (debounced)
  useEffect(() => {
    if (!activeNoteId) return;
    
    // Set status to saving
    setSaveStatus("saving");
    
    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for saving
    saveTimeoutRef.current = setTimeout(() => {
      updateNote(activeNoteId, noteText);
    }, 500); // 500ms debounce
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }
    };
  }, [noteText, activeNoteId]);

  // Toggle blur state
  const toggleBlur = () => {
    setIsBlurred(!isBlurred);
  };

  // Create new note
  const createNewNote = async () => {
    if (!isAuthenticated) {
      setError("Je moet ingelogd zijn om notities op te slaan.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    // Clear any active note
    setActiveNoteId(null);
    setNoteText("");

    try {
      const response = await oneSpaceApi.post('/api/notes', { content: "Nieuwe notitie" });
      console.log('Save Response:', response.data);

      // Handle different response formats for the saved note
      if (response.data) {
        let newNote: Note;
        if (response.data.data && typeof response.data.data === 'object') {
          newNote = response.data.data;
        } else if (typeof response.data === 'object') {
          newNote = response.data;
        } else {
          newNote = { content: "Nieuwe notitie", id: Date.now().toString() };
        }
        
        setSavedNotes([newNote, ...savedNotes]);
        
        // Automatically select the new note
        setActiveNoteId(newNote.id || null);
        setNoteText(newNote.content);
      }
    } catch (err) {
      console.error("Error creating note:", err);
      setError("Kan notitie niet maken. Probeer het later opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update note
  const updateNote = async (noteId: string, content: string) => {
    if (!isAuthenticated || !noteId) return;
    
    setIsSaving(true);
    
    try {
      await oneSpaceApi.put(`/api/notes/${noteId}`, {
        content: content
      });
      
      // Update note in state
      setSavedNotes(savedNotes.map(note => 
        note.id === noteId 
          ? { ...note, content: content }
          : note
      ));
      
      // Show saved indicator
      setSaveStatus("saved");
      
      // Reset save indicator after 2 seconds
      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }
      
      saveIndicatorTimeoutRef.current = setTimeout(() => {
        setSaveStatus("idle");
      }, 2000);
      
    } catch (err) {
      console.error("Error updating note:", err);
      setError("Kan notitie niet bijwerken. Probeer het later opnieuw.");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Select note for editing
  const selectNote = (note: Note) => {
    setActiveNoteId(note.id || null);
    setNoteText(note.content);
    setError(null);
  };
  
  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };
  
  // Open delete confirmation
  const handleDeleteClick = (note: Note, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent note selection
    setNoteToDelete(note);
    setIsDeleteDialogOpen(true);
  };
  
  // Delete note
  const handleDeleteNote = async () => {
    if (!noteToDelete?.id) return;
    
    setIsLoading(true);
    
    try {
      await oneSpaceApi.delete(`/api/notes/${noteToDelete.id}`);
      
      // Get updated notes list
      const updatedNotes = savedNotes.filter(note => note.id !== noteToDelete.id);
      setSavedNotes(updatedNotes);
      
      // Clear active note if deleted
      if (activeNoteId === noteToDelete.id) {
        // Select another note if available
        if (updatedNotes.length > 0) {
          const latestNote = updatedNotes[0];
          setActiveNoteId(latestNote.id || null);
          setNoteText(latestNote.content);
        } else {
          setActiveNoteId(null);
          setNoteText("");
        }
      }
      
      // Close dialog
      setIsDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (err) {
      console.error("Error deleting note:", err);
      setError("Kan notitie niet verwijderen. Probeer het later opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  // Render save status icon
  const renderSaveStatus = () => {
    switch (saveStatus) {
      case "saving":
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
      case "saved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  // Render blurred content
  const renderBlurredContent = (content: string) => {
    return (
      <div className="cursor-pointer select-none" onClick={() => setIsBlurred(false)}>
        <p className="blur-sm select-none hover:blur-[6px] transition-all">
          {content || (
            <span className="text-muted-foreground italic">
              Lege notitie
            </span>
          )}
        </p>
        <div className="flex items-center justify-center mt-2 text-muted-foreground text-xs">
          <EyeOff className="h-3 w-3 mr-1" />
          <span>Klik om te tonen</span>
        </div>
      </div>
    );
  };

  // Fullscreen editor component
  const FullscreenEditor = () => (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">{activeNoteId ? "Notitie bewerken" : "Nieuwe notitie"}</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center mr-4">
            {renderSaveStatus()}
            {saveStatus === "saving" && (
              <span className="text-xs text-muted-foreground ml-2">Opslaan...</span>
            )}
            {saveStatus === "saved" && (
              <span className="text-xs text-green-500 ml-2">Opgeslagen</span>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleBlur}
            className="h-8 w-8"
            title={isBlurred ? "Toon inhoud" : "Verberg inhoud"}
          >
            {isBlurred ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleFullscreen}
            className="h-8 w-8"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setIsFullscreen(false)}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 p-4 overflow-auto">
        {isBlurred ? (
          <div className="w-full h-full min-h-[300px] p-4 border rounded-md" onClick={() => setIsBlurred(false)}>
            {renderBlurredContent(noteText)}
          </div>
        ) : (
          <Textarea
            ref={textareaRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Begin met typen..."
            className="w-full h-full min-h-[300px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0 border-0 shadow-none"
          />
        )}
      </div>
    </div>
  );

  const hasNotes = savedNotes.length > 0;

  return (
    <>
      {isFullscreen && <FullscreenEditor />}
      
      <Card className={isFullscreen ? "hidden" : ""}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleBlur}
              disabled={isLoading}
              title={isBlurred ? "Toon inhoud" : "Verberg inhoud"}
              className="h-7 w-7"
            >
              {isBlurred ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={createNewNote}
              disabled={isLoading}
              title="Nieuwe notitie"
              className="h-7 w-7"
            >
              <Plus className="h-4 w-4" />
            </Button>
            {activeNoteId && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                disabled={isLoading}
                title="Volledig scherm"
                className="h-7 w-7"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {!isAuthenticated ? (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Log in om je notities te bekijken en te beheren.
                </p>
              </div>
            ) : (
              <>
                <div className="relative">
                  {isBlurred && activeNoteId ? (
                    <div className="min-h-[100px] p-3 border rounded-md">
                      {renderBlurredContent(noteText)}
                    </div>
                  ) : (
                    <Textarea
                      ref={textareaRef}
                      placeholder={activeNoteId ? "Bewerk je notitie..." : hasNotes ? "Laden..." : "Maak eerst een notitie aan..."}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      className="min-h-[100px] pr-8"
                      disabled={isLoading || !activeNoteId}
                    />
                  )}
                  {activeNoteId && !isBlurred && (
                    <div className="absolute bottom-2 right-2">
                      {renderSaveStatus()}
                    </div>
                  )}
                </div>
                
                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
                
                <div className="space-y-2 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium">Notities</h3>
                    {isLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    )}
                  </div>
                  
                  {savedNotes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Geen notities gevonden. Maak je eerste notitie!
                    </p>
                  ) : (
                    <div className="max-h-[300px] overflow-y-auto pr-1">
                      {savedNotes.map((note, index) => (
                        <div
                          key={note.id || index}
                          className={`p-3 rounded-md text-sm transition-colors mb-2 flex justify-between items-start cursor-pointer ${
                            activeNoteId === note.id 
                              ? "bg-primary/10 border border-primary/30" 
                              : "bg-muted hover:bg-muted/80"
                          }`}
                          onClick={() => selectNote(note)}
                        >
                          <div className="flex-1 overflow-hidden">
                            <p className={`truncate ${isBlurred ? "blur-sm" : ""}`}>
                              {note.content || (
                                <span className="text-muted-foreground italic">
                                  Lege notitie
                                </span>
                              )}
                            </p>
                            {note.created_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(note.created_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive/90 ml-2 flex-shrink-0"
                            onClick={(e) => handleDeleteClick(note, e)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notitie verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze notitie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default NotesWidget; 